import { useEffect, useState } from "react";
import { getDreGerencialAcumulado } from "../../api";
import { formatCurrency, formatPercent } from "../../utils/orcamento";

// Tabela "Acumulado (YTD)": visão resumida e derivada da mesma árvore do DRE Gerencial —
// não busca dado novo, só soma o intervalo de meses escolhido (ver
// backend/services/dre_gerencial_svc.py::montar_dre_acumulado). Trocar o período aqui NÃO
// afeta a árvore mensal acima — é um fetch independente. Colunas Real/R x O removidas (não
// exibidas) enquanto a fonte de Realizado não estiver conectada; % P.P. vem `null` no mesmo
// cenário e é exibido como "-", nunca 0,00%.
// Ao lado do Planejado vem o fechamento do ano anterior (DRE 2026: realizado nos meses
// fechados, orçado nos demais — tabela realizado_dre_mensal) no mesmo intervalo de meses;
// as colunas só aparecem quando há fechamento para todos os meses do período.

const MESES_NOME = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const HEADER_VINHO = "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)";

function ultimoMesFechado(ano) {
  const hoje = new Date();
  const anoAtual = hoje.getFullYear();
  const mesAtual = hoje.getMonth() + 1;
  if (ano < anoAtual) return 12;
  if (ano > anoAtual) return 1;
  return Math.max(1, mesAtual - 1);
}

function rotuloPeriodo(ano, mesInicio, mesFim) {
  if (mesInicio === mesFim) return `YTD — ${MESES_NOME[mesInicio - 1]} de ${ano}`;
  return `YTD — ${MESES_NOME[mesInicio - 1]} a ${MESES_NOME[mesFim - 1]} de ${ano}`;
}

function valorBrl(v) {
  return v === null || v === undefined ? "-" : formatCurrency(v);
}

function valorPct(v) {
  return v === null || v === undefined ? "-" : formatPercent(v);
}

const LIMITE_PCT = 999; // margem/variação sobre base ~0 vira ruído — exibida como "-"

function valorDelta(v) {
  if (v === null || v === undefined) return "-";
  return `${v > 0 ? "+" : ""}${formatCurrency(v)}`;
}

function valorDeltaPct(v, sufixo = "%") {
  if (v === null || v === undefined || Math.abs(v) > LIMITE_PCT) return "-";
  return `${v > 0 ? "+" : ""}${v.toFixed(sufixo === "%" ? 1 : 2)}${sufixo}`;
}

// Linhas de custo (valor positivo subtraído do resultado): planejar mais que o fechamento é
// desfavorável. Demais linhas (receitas, resultados e deduções, que já vêm negativas): o
// contrário.
const IDS_CUSTO = new Set(["_custos", "sga", "despesas_nao_operacionais", "despesas_financeiras", "impostos"]);

function corDelta(C, id, delta) {
  if (delta === null || delta === undefined || Math.abs(delta) < 0.005) return C.text;
  const favoravel = IDS_CUSTO.has(id) ? delta < 0 : delta > 0;
  return favoravel ? C.success : C.danger;
}

function intervaloMeses(meses) {
  if (!meses.length) return "";
  const abrev = (m) => MESES_NOME[m - 1].slice(0, 3);
  return meses.length === 1 ? abrev(meses[0]) : `${abrev(meses[0])}–${abrev(meses[meses.length - 1])}`;
}

function NotaFechamento({ fechamento, C }) {
  if (!fechamento) return null;
  if (!fechamento.disponivel) {
    return (
      <div style={{ fontSize: 11.5, color: C.muted }}>
        Sem fechamento de {fechamento.ano} para todo o período — comparação indisponível.
      </div>
    );
  }
  return (
    <div style={{ fontSize: 11.5, color: C.muted }}>
      Comparando com o <b>fechamento {fechamento.ano}</b> (DRE {fechamento.ano}) nos mesmos meses
      {fechamento.mesesRealizados.length > 0 && <> · <span style={{ color: C.success, fontWeight: 700 }}>realizado {intervaloMeses(fechamento.mesesRealizados)}</span></>}
      {fechamento.mesesOrcados.length > 0 && <> · <span style={{ color: "#D97706", fontWeight: 700 }}>orçado {intervaloMeses(fechamento.mesesOrcados)} (ainda não fechado)</span></>}
    </div>
  );
}

function celulaNumero(C, valor, extra = {}) {
  const negativo = typeof valor === "number" && valor < 0;
  return {
    padding: "7px 10px", fontSize: 12, textAlign: "right",
    fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
    color: negativo ? C.danger : C.text, borderBottom: `1px solid ${C.border}`,
    ...extra,
  };
}

function thVinho(extra = {}) {
  return {
    padding: "8px 10px", fontSize: 10.5, fontWeight: 700, color: "#fff",
    textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "right",
    whiteSpace: "nowrap", ...extra,
  };
}

function BotaoAtalho({ label, ativo, onClick, C }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: ativo ? C.brand : "transparent",
        border: `1px solid ${ativo ? C.brand : C.border}`,
        color: ativo ? "#fff" : C.text,
        borderRadius: 8, padding: "6px 12px", fontSize: 11.5, fontWeight: 600, cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function SelectMes({ value, onChange, C }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
      style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
        padding: "6px 10px", color: C.text, fontSize: 12.5, fontWeight: 600,
      }}
    >
      {MESES_NOME.map((nome, i) => (
        <option key={nome} value={i + 1}>{nome}</option>
      ))}
    </select>
  );
}

export default function DreGerencialAcumuladoView({ ano, C }) {
  const [mesInicio, setMesInicio] = useState(1);
  const [mesFim, setMesFim] = useState(() => ultimoMesFechado(ano));
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  useEffect(() => {
    setMesInicio(1);
    setMesFim(ultimoMesFechado(ano));
  }, [ano]);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setErro("");
    getDreGerencialAcumulado(ano, mesInicio, mesFim)
      .then((r) => { if (!cancelado) setDados(r); })
      .catch((e) => { if (!cancelado) setErro(e.message); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [ano, mesInicio, mesFim]);

  function handleDe(novoDe) {
    setMesInicio(novoDe);
    if (mesFim < novoDe) setMesFim(novoDe);
  }

  function handleAte(novoAte) {
    setMesFim(novoAte);
    if (mesInicio > novoAte) setMesInicio(novoAte);
  }

  function aplicarAtalho(inicio, fim) {
    setMesInicio(inicio);
    setMesFim(fim);
  }

  const atalhoAtivo = (inicio, fim) => mesInicio === inicio && mesFim === fim;
  const comFechamento = Boolean(dados?.fechamentoAnterior?.disponivel);
  const anoFechamento = dados?.fechamentoAnterior?.ano;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>
          Acumulado (YTD)
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11.5, color: C.muted, fontWeight: 600 }}>De</span>
          <SelectMes value={mesInicio} onChange={handleDe} C={C} />
          <span style={{ fontSize: 11.5, color: C.muted, fontWeight: 600 }}>Até</span>
          <SelectMes value={mesFim} onChange={handleAte} C={C} />
          <div style={{ width: 1, height: 20, background: C.border, margin: "0 4px" }} />
          <BotaoAtalho label="1º Trimestre" ativo={atalhoAtivo(1, 3)} onClick={() => aplicarAtalho(1, 3)} C={C} />
          <BotaoAtalho label="2º Trimestre" ativo={atalhoAtivo(4, 6)} onClick={() => aplicarAtalho(4, 6)} C={C} />
          <BotaoAtalho label="3º Trimestre" ativo={atalhoAtivo(7, 9)} onClick={() => aplicarAtalho(7, 9)} C={C} />
          <BotaoAtalho label="4º Trimestre" ativo={atalhoAtivo(10, 12)} onClick={() => aplicarAtalho(10, 12)} C={C} />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: C.brand }}>
          {rotuloPeriodo(ano, mesInicio, mesFim)}
        </div>
        {!loading && <NotaFechamento fechamento={dados?.fechamentoAnterior} C={C} />}
      </div>

      {erro && (
        <div style={{ color: C.danger, fontSize: 12.5 }}>{erro}</div>
      )}

      {loading ? (
        <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 12.5 }}>Carregando…</div>
      ) : dados && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Tabela 1 — Resultado acumulado */}
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
                <thead>
                  <tr style={{ background: HEADER_VINHO }}>
                    <th style={{ ...thVinho(), textAlign: "left" }}>Resultado</th>
                    {comFechamento && <th style={thVinho()}>Fechamento {anoFechamento}</th>}
                    <th style={thVinho()}>Planejado{comFechamento ? ` ${ano}` : ""}</th>
                    {comFechamento && <th style={thVinho()}>Δ {ano} × {anoFechamento}</th>}
                    {comFechamento && <th style={thVinho()}>Δ %</th>}
                  </tr>
                </thead>
                <tbody>
                  {dados.resultado.map((linha) => (
                    <tr key={linha.id} style={{ background: linha.destaque ? C.bgActive : "transparent" }}>
                      <td style={{
                        padding: "7px 10px", fontSize: 12.5, textAlign: "left", color: C.text,
                        fontWeight: linha.destaque ? 800 : 500, borderBottom: `1px solid ${C.border}`,
                      }}>
                        {linha.label}
                      </td>
                      {comFechamento && (
                        <td style={celulaNumero(C, linha.fechamentoAnterior, { fontWeight: linha.destaque ? 800 : 500 })}>
                          {valorBrl(linha.fechamentoAnterior)}
                        </td>
                      )}
                      <td style={celulaNumero(C, linha.planejado, { fontWeight: linha.destaque ? 800 : 500 })}>
                        {valorBrl(linha.planejado)}
                      </td>
                      {comFechamento && (
                        <td style={celulaNumero(C, null, { color: corDelta(C, linha.id, linha.deltaFechamento), fontWeight: 600 })}>
                          {valorDelta(linha.deltaFechamento)}
                        </td>
                      )}
                      {comFechamento && (
                        <td style={celulaNumero(C, null, { color: corDelta(C, linha.id, linha.deltaFechamento) })}>
                          {valorDeltaPct(linha.deltaFechamentoPct)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tabela 2 — Margens acumuladas */}
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
                <thead>
                  <tr style={{ background: HEADER_VINHO }}>
                    <th style={{ ...thVinho(), textAlign: "left" }}>Margens</th>
                    {comFechamento && <th style={thVinho()}>Fechamento {anoFechamento}</th>}
                    <th style={thVinho()}>Planejado{comFechamento ? ` ${ano}` : ""}</th>
                    {comFechamento && <th style={thVinho()}>Δ p.p.</th>}
                  </tr>
                </thead>
                <tbody>
                  {dados.margens.map((linha) => (
                    <tr key={linha.id}>
                      <td style={{
                        padding: "7px 10px", fontSize: 12.5, textAlign: "left", color: C.text,
                        fontWeight: 700, borderBottom: `1px solid ${C.border}`,
                      }}>
                        {linha.label}
                      </td>
                      {comFechamento && (
                        <td style={celulaNumero(C, linha.fechamentoAnterior)}>
                          {valorPct(linha.fechamentoAnterior)}
                        </td>
                      )}
                      <td style={celulaNumero(C, linha.planejado)}>
                        {Math.abs(linha.planejado) > LIMITE_PCT ? "-" : valorPct(linha.planejado)}
                      </td>
                      {comFechamento && (
                        <td style={celulaNumero(C, null, { color: corDelta(C, linha.id, linha.ppDeltaFechamento) })}>
                          {valorDeltaPct(linha.ppDeltaFechamento, " p.p.")}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
